import React, { useCallback, useEffect, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Link,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Logger from "../../../services/Logger";
import S3Service from "../../../services/S3Service";
import SocialTabs from "../../advertisement/social/SocialTabs";
import InstagramService from "./InstagramService";

const GRAPH_API_EXPLORER_URL = "https://developers.facebook.com/tools/explorer/";

function isFailureResponse(data) {
    return Boolean(data && data.code === "FAILURE");
}

function isTokenExpiredMessage(message) {
    if (!message || typeof message !== "string") return false;
    const m = message.toLowerCase();
    return m.includes("session has expired")
        || m.includes("error_subcode\":463")
        || m.includes("\"code\":190")
        || m.includes("code\":190")
        || m.includes("invalid oauth")
        || m.includes("error validating access token")
        || m.includes("cannot parse access token")
        || m.includes("the access token you provided has expired");
}

function formatJson(data) {
    if (data == null) return "";
    return typeof data === "string" ? data : JSON.stringify(data, null, 2);
}

function formatTokenExpiry(expiresAt) {
    if (expiresAt == null || expiresAt === 0) return "No expiry (long-lived)";
    const ms = expiresAt * 1000;
    if (!Number.isFinite(ms)) return String(expiresAt);
    return new Date(ms).toLocaleString();
}

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        Logger.error("Clipboard copy failed:", err);
        return false;
    }
}

function JsonBlock({ value }) {
    if (!value) return null;
    return (
        <TextField
            value={formatJson(value)}
            fullWidth
            multiline
            minRows={4}
            maxRows={16}
            InputProps={{ readOnly: true }}
            variant="outlined"
            size="small"
            sx={{ "& textarea": { fontFamily: "ui-monospace, monospace", fontSize: 13 } }}
        />
    );
}

export default function TestInstagramMain() {
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);

    const [me, setMe] = useState(null);
    const [meLoading, setMeLoading] = useState(false);
    const [meError, setMeError] = useState(null);

    const [debugToken, setDebugToken] = useState(null);
    const [debugLoading, setDebugLoading] = useState(false);
    const [debugError, setDebugError] = useState(null);

    const [recipientId, setRecipientId] = useState("");
    const [message, setMessage] = useState("Hello from Puzzle Interact admin test.");
    const [sendLoading, setSendLoading] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [sendError, setSendError] = useState(null);

    const [imageUrl, setImageUrl] = useState("");
    const [imageCaption, setImageCaption] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const [postImageLoading, setPostImageLoading] = useState(false);
    const [postImageResult, setPostImageResult] = useState(null);
    const [postImageError, setPostImageError] = useState(null);

    const [copyHint, setCopyHint] = useState("");

    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        setStatusError(null);
        try {
            const result = await InstagramService.getStatus();
            if (isFailureResponse(result)) {
                setStatus(null);
                setStatusError(result.message || "Failed to load status");
            } else {
                setStatus(result);
            }
        } catch (err) {
            Logger.error("Instagram status error:", err);
            setStatus(null);
            setStatusError(err.message || "Failed to load status");
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const loadMe = useCallback(async () => {
        setMeLoading(true);
        setMeError(null);
        setMe(null);
        try {
            const result = await InstagramService.getMe();
            if (isFailureResponse(result)) {
                setMeError(result.message || "Failed to load account profile");
            } else {
                setMe(result);
            }
        } catch (err) {
            Logger.error("Instagram /me error:", err);
            setMeError(err.message || "Failed to load account profile");
        } finally {
            setMeLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
        loadMe();
    }, [loadStatus, loadMe]);

    const loadDebugToken = async () => {
        setDebugLoading(true);
        setDebugError(null);
        setDebugToken(null);
        try {
            const result = await InstagramService.getDebugToken();
            if (isFailureResponse(result)) {
                setDebugError(result.message || "Failed to debug token");
            } else {
                setDebugToken(result);
            }
        } catch (err) {
            Logger.error("Instagram debug-token error:", err);
            setDebugError(err.message || "Failed to debug token");
        } finally {
            setDebugLoading(false);
        }
    };

    const handleSend = async () => {
        const rid = recipientId.trim();
        const msg = message.trim();
        if (!rid) {
            setSendError("Recipient ID is required (Instagram-scoped user id from webhook).");
            return;
        }
        if (!msg) {
            setSendError("Message is required.");
            return;
        }

        setSendLoading(true);
        setSendError(null);
        setSendResult(null);
        try {
            const result = await InstagramService.sendMessage(rid, msg);
            if (isFailureResponse(result)) {
                setSendError(result.message || "Failed to send message");
            } else {
                setSendResult(result);
            }
        } catch (err) {
            Logger.error("Instagram send-message error:", err);
            setSendError(err.message || "Failed to send message");
        } finally {
            setSendLoading(false);
        }
    };

    const clearImagePreview = () => {
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
        }
        setImagePreviewUrl(null);
    };

    const handleImageFileSelect = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setPostImageError("Please select an image file.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setPostImageError("Image must be 8 MB or smaller.");
            return;
        }

        clearImagePreview();
        setImageFile(file);
        setImagePreviewUrl(URL.createObjectURL(file));
        setImageUrl("");
        setPostImageError(null);
        setPostImageResult(null);
    };

    const handleUploadImage = async () => {
        if (!imageFile) {
            setPostImageError("Select an image file first.");
            return;
        }

        setImageUploadLoading(true);
        setPostImageError(null);
        try {
            const ext = imageFile.name.split(".").pop() || "jpg";
            const fileName = `instagram-test-${Date.now()}.${ext}`;
            const uploaded = await S3Service.uploadToS3(imageFile, fileName, "instagram-test");
            setImageUrl(uploaded.url);
        } catch (err) {
            Logger.error("Instagram test image upload error:", err);
            setPostImageError(err.message || "Failed to upload image");
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handlePostImage = async () => {
        const url = imageUrl.trim();
        if (!url) {
            setPostImageError("Upload an image or paste a public HTTPS image URL.");
            return;
        }
        if (!url.startsWith("https://")) {
            setPostImageError("imageUrl must be a public HTTPS URL Meta can fetch.");
            return;
        }

        setPostImageLoading(true);
        setPostImageError(null);
        setPostImageResult(null);
        try {
            const result = await InstagramService.postImage(url, imageCaption);
            if (isFailureResponse(result)) {
                setPostImageError(result.message || "Failed to publish image");
            } else {
                setPostImageResult(result);
            }
        } catch (err) {
            Logger.error("Instagram post-image error:", err);
            setPostImageError(err.message || "Failed to publish image");
        } finally {
            setPostImageLoading(false);
        }
    };

    const handleCopy = async (label, text) => {
        if (!text) return;
        const ok = await copyText(text);
        setCopyHint(ok ? `Copied ${label}` : `Could not copy ${label}`);
        setTimeout(() => setCopyHint(""), 2500);
    };

    const tokenData = debugToken?.data ?? null;

    return (
        <Box sx={{ maxWidth: 960, mx: "auto", p: { xs: 1, sm: 2 } }}>
            <SocialTabs />

            <Typography variant="h5" gutterBottom>
                Test Instagram
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Verify the Puzzle Interact-IG Meta app connection, publish test images to your Instagram feed, inspect
                token scopes, and send test DMs via <code>/test/instagram</code>.
            </Typography>

            {copyHint ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {copyHint}
                </Alert>
            ) : null}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Typography variant="h6">Connection status</Typography>
                                <Button
                                    size="small"
                                    startIcon={statusLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                                    onClick={loadStatus}
                                    disabled={statusLoading}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {statusError ? <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert> : null}

                            {statusLoading && !status ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">Loading…</Typography>
                                </Box>
                            ) : null}

                            {status ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        <Chip
                                            label={status.enabled ? "Enabled" : "Disabled"}
                                            color={status.enabled ? "success" : "default"}
                                            size="small"
                                        />
                                        <Chip
                                            label={status.ready ? "Ready" : "Not ready"}
                                            color={status.ready ? "success" : "warning"}
                                            size="small"
                                        />
                                    </Box>
                                    {status.readinessGap ? (
                                        <Alert severity="warning">{status.readinessGap}</Alert>
                                    ) : null}
                                    {status.webhookUrl ? (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Webhook URL (Meta Developer Console)
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                <Typography variant="body2" sx={{ wordBreak: "break-all", flex: 1 }}>
                                                    {status.webhookUrl}
                                                </Typography>
                                                <Tooltip title="Copy webhook URL">
                                                    <IconButton size="small" onClick={() => handleCopy("webhook URL", status.webhookUrl)}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ) : null}
                                    {status.webhookVerifyToken ? (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Webhook verify token
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontFamily: "ui-monospace, monospace" }}>
                                                    {status.webhookVerifyToken}
                                                </Typography>
                                                <Tooltip title="Copy verify token">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopy("verify token", status.webhookVerifyToken)}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ) : null}
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Typography variant="h6">Connected account</Typography>
                                <Button size="small" onClick={loadMe} disabled={meLoading}>
                                    {meLoading ? "Loading…" : "Fetch /me"}
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Proxies Meta Graph <code>GET /me</code> — use the account id when debugging sends.
                            </Typography>
                            {meError ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {isTokenExpiredMessage(meError)
                                        ? "Access token expired or invalid. Instagram posting will fail until you regenerate it."
                                        : meError}
                                </Alert>
                            ) : null}
                            <Alert severity="info" sx={{ mb: me ? 2 : 0 }}>
                                If the token expired, open{" "}
                                <Link
                                    href={GRAPH_API_EXPLORER_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Graph API Explorer
                                </Link>
                                , pick the Puzzle Interact-IG app, generate a token, then{" "}
                                <code>GET /me/accounts</code> and copy the Page access token into{" "}
                                <code>INSTAGRAM_ACCESS_TOKEN</code>. Restart the backend after updating it.
                            </Alert>
                            {me ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                    <Typography variant="body2"><strong>ID:</strong> {me.id ?? "—"}</Typography>
                                    <Typography variant="body2"><strong>Username:</strong> @{me.username ?? "—"}</Typography>
                                    <Typography variant="body2"><strong>Name:</strong> {me.name ?? "—"}</Typography>
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Typography variant="h6">Access token</Typography>
                                <Button size="small" onClick={loadDebugToken} disabled={debugLoading}>
                                    {debugLoading ? "Loading…" : "Debug token"}
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Inspect scopes and expiry before publishing or sending test messages. Feed posts require{" "}
                                <code>instagram_content_publish</code>.
                            </Typography>
                            {debugError ? <Alert severity="error" sx={{ mb: 2 }}>{debugError}</Alert> : null}
                            {tokenData ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                    <Typography variant="body2">
                                        <strong>Valid:</strong>{" "}
                                        <Chip
                                            label={tokenData.is_valid ? "Yes" : "No"}
                                            color={tokenData.is_valid ? "success" : "error"}
                                            size="small"
                                        />
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Expires:</strong> {formatTokenExpiry(tokenData.expires_at)}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>App:</strong> {tokenData.application ?? tokenData.app_id ?? "—"}
                                    </Typography>
                                    {Array.isArray(tokenData.scopes) && tokenData.scopes.length > 0 ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                            {tokenData.scopes.map((scope) => (
                                                <Chip key={scope} label={scope} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    ) : null}
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Send test message
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Recipient must have messaged your Instagram professional account first (24-hour window).
                                Use <code>entry[].messaging[].sender.id</code> from webhook logs as the recipient id.
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    label="Recipient ID (IGSID)"
                                    value={recipientId}
                                    onChange={(e) => {
                                        setRecipientId(e.target.value);
                                        setSendError(null);
                                    }}
                                    fullWidth
                                    size="small"
                                    placeholder="1234567890123456"
                                    disabled={sendLoading}
                                />
                                <TextField
                                    label="Message"
                                    value={message}
                                    onChange={(e) => {
                                        setMessage(e.target.value);
                                        setSendError(null);
                                    }}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    size="small"
                                    disabled={sendLoading}
                                />
                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={handleSend}
                                        disabled={sendLoading || !status?.ready}
                                    >
                                        {sendLoading ? "Sending…" : "Send test DM"}
                                    </Button>
                                    {!status?.ready && status && !statusLoading ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                                            Integration must be ready before sending.
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>

                            {sendError ? <Alert severity="error" sx={{ mt: 2 }}>{sendError}</Alert> : null}
                            {sendResult ? (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="success" sx={{ mb: 1 }}>
                                        Message sent — message id: {sendResult.message_id ?? "—"}
                                    </Alert>
                                    <JsonBlock value={sendResult} />
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Publish image to feed
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Upload an image to S3 (public HTTPS URL) or paste an existing URL, then publish via{" "}
                                <code>POST /test/instagram/post-image</code>. No recipient id — posts to your connected
                                Instagram account feed. Requires <code>instagram_content_publish</code> on the access token.
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<ImageIcon />}
                                        disabled={imageUploadLoading || postImageLoading}
                                    >
                                        Choose image
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={handleImageFileSelect}
                                        />
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={
                                            imageUploadLoading ? <CircularProgress size={16} /> : <CloudUploadIcon />
                                        }
                                        onClick={handleUploadImage}
                                        disabled={!imageFile || imageUploadLoading || postImageLoading}
                                    >
                                        {imageUploadLoading ? "Uploading…" : "Upload to S3"}
                                    </Button>
                                    {imageFile ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {imageFile.name} ({Math.round(imageFile.size / 1024)} KB)
                                        </Typography>
                                    ) : null}
                                </Box>

                                {(imagePreviewUrl || imageUrl) ? (
                                    <Box
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            p: 1,
                                            maxWidth: 360,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={imageUrl || imagePreviewUrl}
                                            alt="Preview"
                                            sx={{
                                                display: "block",
                                                maxWidth: "100%",
                                                maxHeight: 240,
                                                objectFit: "contain",
                                            }}
                                        />
                                    </Box>
                                ) : null}

                                <TextField
                                    label="Image URL (public HTTPS)"
                                    value={imageUrl}
                                    onChange={(e) => {
                                        setImageUrl(e.target.value);
                                        setPostImageError(null);
                                    }}
                                    fullWidth
                                    size="small"
                                    placeholder="https://puzzleinteract-images.s3.us-east-1.amazonaws.com/..."
                                    disabled={postImageLoading}
                                    helperText="Meta fetches this URL directly — localhost and http:// are rejected."
                                />

                                <TextField
                                    label="Caption (optional)"
                                    value={imageCaption}
                                    onChange={(e) => {
                                        setImageCaption(e.target.value);
                                        setPostImageError(null);
                                    }}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    size="small"
                                    placeholder="Optional caption #puzzle"
                                    disabled={postImageLoading}
                                />

                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={handlePostImage}
                                        disabled={postImageLoading || !status?.ready}
                                    >
                                        {postImageLoading ? "Publishing…" : "Publish to Instagram feed"}
                                    </Button>
                                    {!status?.ready && status && !statusLoading ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                                            Integration must be ready before publishing.
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>

                            {postImageError ? <Alert severity="error" sx={{ mt: 2 }}>{postImageError}</Alert> : null}
                            {postImageResult ? (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="success" sx={{ mb: 1 }}>
                                        Published — media id:{" "}
                                        {postImageResult.published?.id ??
                                            postImageResult.id ??
                                            postImageResult.creation_id ??
                                            "—"}
                                    </Alert>
                                    <JsonBlock value={postImageResult} />
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
